import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Users,
  Edit2,
  Trash2,
  X,
  Phone,
  FileText,
  AlertCircle,
  Power
} from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Operators({ userRole = 'admin' }) {
  const canEdit = ['admin', 'gestor'].includes(userRole);
  
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [form, setForm] = useState({
    name: '',
    document: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      const response = await fetch(`${API_URL}/api/operators`, {
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setOperators(data);
      } else {
        toast.error('Erro ao carregar operadores');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = editingOperator 
      ? `${API_URL}/api/operators/${editingOperator.id}`
      : `${API_URL}/api/operators`;
    
    try {
      const response = await fetch(url, {
        method: editingOperator ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(editingOperator ? 'Operador atualizado!' : 'Operador cadastrado!');
        setShowModal(false);
        resetForm();
        fetchOperators();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este operador?')) return;

    try {
      const response = await fetch(`${API_URL}/api/operators/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (response.ok) {
        toast.success('Operador excluído!');
        fetchOperators();
      } else {
        toast.error('Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const handleToggleStatus = async (operator) => {
    const action = operator.status === 'active' ? 'inativar' : 'ativar';
    if (!window.confirm(`Deseja ${action} o operador ${operator.name}?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/operators/${operator.id}/toggle-status`, {
        method: 'PATCH',
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchOperators();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao alterar status');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const openEditModal = (operator) => {
    setEditingOperator(operator);
    setForm({
      name: operator.name,
      document: operator.document,
      phone: operator.phone || '',
      notes: operator.notes || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingOperator(null);
    setForm({
      name: '',
      document: '',
      phone: '',
      notes: ''
    });
  };

  const filteredOperators = operators.filter(o => 
    o.name.toLowerCase().includes(search.toLowerCase()) ||
    o.document.toLowerCase().includes(search.toLowerCase())
  );

  const formatDocument = (doc) => {
    // Formatar CPF: 000.000.000-00
    const cleaned = doc.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return doc;
  };

  return (
    <div className="space-y-6" data-testid="operators-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Operadores</h1>
          <p className="text-gray-400 mt-1">
            {canEdit ? 'Gerencie seus operadores' : 'Visualize os operadores'}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            data-testid="add-operator-button"
            className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
          >
            <Plus size={20} />
            <span>Novo Operador</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Buscar por nome ou documento..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
          data-testid="search-input"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredOperators.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredOperators.map((operator) => (
            <div 
              key={operator.id} 
              className={`bg-[#111] border border-gray-800 rounded-xl p-4 lg:p-6 hover:border-gray-700 transition-all ${operator.status !== 'active' ? 'opacity-60' : ''}`}
              data-testid={`operator-card-${operator.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold text-lg">
                    {operator.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{operator.name}</h3>
                    <p className="text-gray-500 text-sm font-mono">{formatDocument(operator.document)}</p>
                  </div>
                </div>
                {canEdit ? (
                  <button
                    onClick={() => handleToggleStatus(operator)}
                    data-testid={`toggle-status-operator-${operator.id}`}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded cursor-pointer transition-colors ${
                      operator.status === 'active' 
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                        : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    }`}
                    title={operator.status === 'active' ? 'Clique para inativar' : 'Clique para ativar'}
                  >
                    <Power size={12} />
                    {operator.status === 'active' ? 'Ativo' : 'Inativo'}
                  </button>
                ) : (
                  <span className={`text-xs px-2 py-1 rounded ${
                    operator.status === 'active' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {operator.status === 'active' ? 'Ativo' : 'Inativo'}
                  </span>
                )}
              </div>

              {(operator.phone || operator.notes) && (
                <div className="space-y-2 mb-4">
                  {operator.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <Phone size={14} />
                      <span>{operator.phone}</span>
                    </div>
                  )}
                  {operator.notes && (
                    <div className="flex items-start gap-2 text-sm text-gray-400">
                      <FileText size={14} className="mt-0.5" />
                      <span className="line-clamp-2">{operator.notes}</span>
                    </div>
                  )}
                </div>
              )}

              {canEdit && (
                <div className="flex gap-2 pt-4 border-t border-gray-800">
                  <button
                    onClick={() => openEditModal(operator)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors text-sm"
                    data-testid={`edit-operator-${operator.id}`}
                  >
                    <Edit2 size={16} />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(operator.id)}
                    className="flex items-center justify-center px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                    data-testid={`delete-operator-${operator.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#111] border border-gray-800 rounded-xl p-12 text-center">
          <AlertCircle size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-white text-lg font-semibold mb-2">Nenhum operador encontrado</h3>
          <p className="text-gray-500 mb-6">
            {search ? 'Tente ajustar sua busca' : (canEdit ? 'Comece cadastrando seu primeiro operador' : 'Nenhum operador cadastrado ainda')}
          </p>
          {!search && canEdit && (
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
            >
              <Plus size={20} />
              Cadastrar Operador
            </button>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-800">
              <h2 className="text-xl font-semibold text-white">
                {editingOperator ? 'Editar Operador' : 'Novo Operador'}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Nome Completo *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
                  placeholder="Ex: João da Silva"
                  required
                  data-testid="input-name"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">CPF/Documento *</label>
                <input
                  type="text"
                  value={form.document}
                  onChange={(e) => setForm({...form, document: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors font-mono"
                  placeholder="000.000.000-00"
                  required
                  data-testid="input-document"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Telefone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({...form, phone: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
                  placeholder="(00) 00000-0000"
                  data-testid="input-phone"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Observações</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors resize-none"
                  placeholder="Informações adicionais..."
                  rows={3}
                  data-testid="input-notes"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
                  data-testid="submit-operator"
                >
                  {editingOperator ? 'Salvar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
